import { c as _c } from "react/compiler-runtime";
import type { MergeMethod } from '@/lib/pull-requests/types';
import { Check, ChevronDown, ExternalLink } from 'lucide-react';
import type { Dispatch, RefObject, SetStateAction } from 'react';
import type { PrimaryButtonConfig } from './pr-v2-merge-choices';
import { getMethodLabel } from './pr-v2-merge-choices';
import { badgeVariants } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
type PrV2MergeActionControlsProps = {
  primaryButtonRef: RefObject<HTMLButtonElement | null>;
  methodPickerTriggerRef: RefObject<HTMLButtonElement | null>;
  primaryButtonConfig: PrimaryButtonConfig;
  primaryAction: () => void;
  closeMenu: () => void;
  state: {
    canPickMergeMethod: boolean;
    isPending: boolean;
    isMethodPickerOpen: boolean;
    canBypassRules: boolean;
    checkedBypassRules: boolean;
  };
  setIsMethodPickerOpen: Dispatch<SetStateAction<boolean>>;
  selectableMergeMethods: MergeMethod[];
  effectiveMergeMethod: MergeMethod;
  selectMergeMethod: (method: MergeMethod) => void;
  setCheckedBypassRules: Dispatch<SetStateAction<boolean>>;
};
export function PrV2MergeActionControls(t0) {
  const $ = _c(35);
  const {
    primaryButtonRef,
    methodPickerTriggerRef,
    primaryButtonConfig,
    primaryAction,
    closeMenu,
    state,
    setIsMethodPickerOpen,
    selectableMergeMethods,
    effectiveMergeMethod,
    selectMergeMethod,
    setCheckedBypassRules
  } = t0;
  const {
    canPickMergeMethod,
    isPending,
    isMethodPickerOpen,
    canBypassRules,
    checkedBypassRules
  } = state;
  const {
    action
  } = primaryButtonConfig;
  let t1;
  if ($[0] !== action.kind || $[1] !== action.stackCount || $[2] !== action.url || $[3] !== canPickMergeMethod || $[4] !== closeMenu || $[5] !== primaryAction || $[6] !== primaryButtonConfig.disabled || $[7] !== primaryButtonConfig.text || $[8] !== primaryButtonRef) {
    t1 = action.kind === "view-queue" && action.url ? <Button ref={primaryButtonRef} asChild={true} variant="outline" className="w-full"><a href={action.url} target="_blank" rel="noopener noreferrer" onClick={closeMenu}>{primaryButtonConfig.text}<ExternalLink /></a></Button> : <Button ref={primaryButtonRef} type="button" className={`w-full bg-green-800 text-white hover:bg-green-800/90 disabled:opacity-50 disabled:cursor-not-allowed ${canPickMergeMethod ? "rounded-r-none" : ""}`} disabled={primaryButtonConfig.disabled} onClick={primaryAction}>{primaryButtonConfig.text}{action.kind === "merge" && action.stackCount ? <span className={cn(badgeVariants({
        variant: "secondary"
      }), "px-1.5 py-0 tabular-nums")}>{action.stackCount}</span> : null}</Button>;
    $[0] = action.kind;
    $[1] = action.stackCount;
    $[2] = action.url;
    $[3] = canPickMergeMethod;
    $[4] = closeMenu;
    $[5] = primaryAction;
    $[6] = primaryButtonConfig.disabled;
    $[7] = primaryButtonConfig.text;
    $[8] = primaryButtonRef;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  let t2;
  if ($[10] !== canPickMergeMethod || $[11] !== isMethodPickerOpen || $[12] !== isPending || $[13] !== methodPickerTriggerRef || $[14] !== setIsMethodPickerOpen) {
    t2 = canPickMergeMethod ? <Button ref={methodPickerTriggerRef} type="button" aria-label="Change merge method" aria-haspopup="menu" aria-expanded={isMethodPickerOpen} className="w-9 shrink-0 rounded-l-none border-l border-black/30 bg-green-800 px-0 text-white hover:bg-green-800/90 disabled:opacity-50 disabled:cursor-not-allowed" disabled={isPending} onClick={() => setIsMethodPickerOpen(_temp2)}><ChevronDown className="size-3.5" /></Button> : null;
    $[10] = canPickMergeMethod;
    $[11] = isMethodPickerOpen;
    $[12] = isPending;
    $[13] = methodPickerTriggerRef;
    $[14] = setIsMethodPickerOpen;
    $[15] = t2;
  } else {
    t2 = $[15];
  }
  let t3;
  if ($[16] !== canPickMergeMethod || $[17] !== effectiveMergeMethod || $[18] !== isMethodPickerOpen || $[19] !== methodPickerTriggerRef || $[20] !== selectMergeMethod || $[21] !== selectableMergeMethods || $[22] !== setIsMethodPickerOpen) {
    t3 = canPickMergeMethod && isMethodPickerOpen ? <div role="menu" aria-label="Merge method" data-testid="pr-v2-merge-method-picker" className="absolute right-0 bottom-full z-50 mb-1 w-52 overflow-hidden rounded-md border border-border bg-popover p-1 text-popover-foreground shadow-md">{selectableMergeMethods.map(method => <button key={method} type="button" role="menuitemradio" aria-checked={method === effectiveMergeMethod} className="relative flex w-full cursor-pointer items-center rounded-sm py-1.5 pr-2 pl-8 text-left text-sm transition-colors outline-hidden select-none hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground" onClick={() => {
        selectMergeMethod(method);
        setIsMethodPickerOpen(false);
        methodPickerTriggerRef.current?.focus();
      }}><span className="absolute left-2 flex size-3.5 items-center justify-center">{method === effectiveMergeMethod ? <Check className="size-4" /> : null}</span>{getMethodLabel(method)}</button>)}</div> : null;
    $[16] = canPickMergeMethod;
    $[17] = effectiveMergeMethod;
    $[18] = isMethodPickerOpen;
    $[19] = methodPickerTriggerRef;
    $[20] = selectMergeMethod;
    $[21] = selectableMergeMethods;
    $[22] = setIsMethodPickerOpen;
    $[23] = t3;
  } else {
    t3 = $[23];
  }
  let t4;
  if ($[24] !== t1 || $[25] !== t2 || $[26] !== t3) {
    t4 = <div className="relative mt-4 flex">{t1}{t2}{t3}</div>;
    $[24] = t1;
    $[25] = t2;
    $[26] = t3;
    $[27] = t4;
  } else {
    t4 = $[27];
  }
  let t5;
  if ($[28] !== canBypassRules || $[29] !== checkedBypassRules || $[30] !== setCheckedBypassRules) {
    t5 = canBypassRules ? <div className="mt-3 flex items-center space-x-2"><Checkbox id="pr-v2-bypass-rules" checked={checkedBypassRules} onCheckedChange={checked => setCheckedBypassRules(checked === true)} /><Label htmlFor="pr-v2-bypass-rules" className={`cursor-pointer peer-disabled:opacity-100 text-sm transition-colors ${checkedBypassRules ? "text-zinc-300" : "text-zinc-500 hover:text-zinc-400"}`}>Merge using admin privileges to bypass rules</Label></div> : null;
    $[28] = canBypassRules;
    $[29] = checkedBypassRules;
    $[30] = setCheckedBypassRules;
    $[31] = t5;
  } else {
    t5 = $[31];
  }
  let t6;
  if ($[32] !== t4 || $[33] !== t5) {
    t6 = <div className="sticky bottom-0 px-4" onKeyDown={_temp}>{t4}{t5}</div>;
    $[32] = t4;
    $[33] = t5;
    $[34] = t6;
  } else {
    t6 = $[34];
  }
  return t6;
}
function _temp2(current) {
  return !current;
}
function _temp(event) {
  if (event.key === "Tab") {
    event.stopPropagation();
  }
}